//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { BodyText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import { Mixins } from '@theme/mixins';
import { pluralize } from '@utils/stringUtils';

interface LeaveUnitsBehindModalProps {
  closeModal: () => void;
  isModalOpen: boolean;
  onContinue: () => void;
  onReturnToProducts: () => void;
  remainingUnits: number;
}

export const LeaveUnitsBehindModal = ({
  closeModal,
  isModalOpen,
  onContinue,
  onReturnToProducts,
  remainingUnits,
}: LeaveUnitsBehindModalProps) => {
  return (
    <Modal
      closeModal={closeModal}
      contents={
        <ContentWrapper>
          <BodyText>
            It looks like you are using a <Bold>Single Use</Bold> purchase
            option and you have not allocated all of your available units.
          </BodyText>
          <BodyText>
            If you don't allocate your remaining&nbsp;
            <Bold>{pluralize(remainingUnits, 'unit')}</Bold>, you will lose
            them. We recommend using all of your units to get the full value
            from this purchase option.
          </BodyText>
          <BodyText>Would you like to update any product details?</BodyText>
        </ContentWrapper>
      }
      isModalOpen={isModalOpen}
      primaryButtonInfo={{
        buttonContent: 'Return To Products',
        onClick: onReturnToProducts,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.LEAVE_UNITS_BEHIND_MODAL_RETURN_BUTTON,
      }}
      secondaryButtonInfo={{
        buttonContent: 'Continue',
        onClick: onContinue,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.LEAVE_UNITS_BEHIND_MODAL_CONTINUE_BUTTON,
      }}
      shouldIncludeSecondaryButton={true}
      title='Leave Units Behind?'
      width='medium'
    />
  );
};

const ContentWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[4]};
`,
);

const Bold = styled.span`
  ${Mixins.Text.BodyBold}
`;
